import React, { useCallback, useMemo, useState } from 'react';
import { InlineDatePicker, makeStyles, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import moment, { Moment } from 'moment';

import { TimeSlotDict } from '../../../api/api';
import { Layout } from '../../../components/Layout';
import { ScrollableContent } from '../../../components/ScrollableContent';
import { StepProgressBar } from '../../../components/StepProgressBar';
import { Codeowners } from '../../../consts/codeowners';
import { useChangeStep } from '../../../hooks/useChangeStep';
import { useOrgData } from '../../../hooks/useOrgData';
import { useTimeSlotsDict } from '../../../hooks/useTimeSlots';
import { useOnlineBookingReducer } from '../../../state/useOnlineBookingReducer';
import { getPricingFormItemsFromCart, getServiceItemsFromCart } from '../../../state/utils';
import { getDaysInMonth } from '../../../utils/dateUtils';
import { captureException } from '../../../utils/exception';
import { BackArrowButton } from '../../BackArrowButton';
import { WarningMessage } from '../../WarningMessage';

import { DayPicker } from './DayPicker';
import { TimeSlots } from './TimeSlots';

const useStyles = makeStyles((theme: HCPTheme) => ({
  input: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '4px',
    boxShadow:
      '0px 0px 12px 2px rgba(0, 0, 0, .1), 0px 0px 0px 0px rgba(0, 0, 0, .1), 0px 0px 0px 0px rgba(0, 0, 0, .1)',
    '& fieldset': {
      border: 'none',
    },
  },
}));

const dateFormat = 'dddd Do, MMMM YYYY';

const earliestDate = (previousDate: string, nextDate: string) =>
  new Date(previousDate).getTime() - new Date(nextDate).getTime();

const findFirstAvailableDate = (timeSlotsDict: TimeSlotDict) => {
  return Object.keys(timeSlotsDict)
    .sort(earliestDate)
    .find((date) => timeSlotsDict[date]?.length);
};

const findDateToSelect = (timeSlotsDict: TimeSlotDict) => {
  const currentDate = moment(new Date()).format('YYYY-MM-DD');

  if (timeSlotsDict[currentDate]?.length) {
    return currentDate;
  }

  const dateToSelect = findFirstAvailableDate(timeSlotsDict);

  return dateToSelect || null;
};

export const ArrivalStepLegacy = () => {
  const [isOpen, setIsOpen] = useState(false);
  const MIN_DATE = useMemo(() => new Date(), []);
  const MAX_DATE = useMemo(() => {
    const result = new Date(MIN_DATE);
    result.setMonth(MIN_DATE.getMonth() + 2);
    result.setDate(getDaysInMonth(result));
    return result;
  }, [MIN_DATE]);

  const { state, dispatch, actions } = useOnlineBookingReducer();
  const { changeStep } = useChangeStep();

  const { data: orgData } = useOrgData({ token: state.token });
  const selectedDate = state.arrivalWindow.date;

  const setDate = useCallback(
    (date: string) => {
      dispatch(actions.selectArrivalDate(date));
      dispatch(actions.resetArrivalTime());
      dispatch(actions.setUnavailableDateError(false));
    },
    [dispatch, actions]
  );

  const services = getServiceItemsFromCart(state).map((cartItem) => ({
    uuid: cartItem.service.uuid,
    quantity: cartItem.quantity,
  }));

  const pricingForms = getPricingFormItemsFromCart(state).map((cartItem) => ({
    uuid: cartItem.pricingForm.uuid,
  }));

  const {
    data: timeSlotsDict,
    isError,
    isFetched,
  } = useTimeSlotsDict({
    token: state.token,
    dateFrom: MIN_DATE,
    dateTo: MAX_DATE,
    services,
    pricingForms,
    onSuccess: (data) => {
      if (data && (!selectedDate || !data[selectedDate])) {
        const dateToSelect = findDateToSelect(data);
        if (dateToSelect) {
          setDate(dateToSelect);
        } else {
          captureException('Cannot select a date to set', Codeowners.CUSTOMER_INTAKE, {
            minDate: MIN_DATE.toString(),
            maxDate: MAX_DATE.toString(),
          });
          dispatch(actions.setUnavailableDateError(true));
        }
      }
    },
  });

  const { input } = useStyles();

  const shouldDisableDate = (day: Moment) => {
    return timeSlotsDict ? !timeSlotsDict[day.format('YYYY-MM-DD')]?.length : false;
  };

  const onDateChange = useCallback(
    (newDate: Moment | null) => {
      setDate(newDate ? newDate.format('YYYY-MM-DD') : '');
      setIsOpen(false);
    },
    [setDate]
  );

  const isExactTime = orgData?.defaultArrivalWindow === 0;

  const timeslotsUnavailable = isError || (isFetched && state.unavailableDateError);

  const isNextButtonDisabled = !(state.arrivalWindow.date && state.arrivalWindow.time);

  const handleBackClick = () => {
    dispatch(actions.hideWarningMessage());
    changeStep('contact-details');
    dispatch(actions.resetArrivalTime());
  };

  const handleNextClick = () => {
    dispatch(actions.hideWarningMessage());
    changeStep('confirmation');
  };

  return (
    <Layout
      Header={<StepProgressBar currentStep={4} stepCount={5} />}
      ContentComponent={
        <>
          <WarningMessage />
          <BackArrowButton onClick={handleBackClick} />
          <Typography gutterBottom variant="h5">
            Pick an arrival window
          </Typography>
          <ScrollableContent>
            <>
              <InlineDatePicker
                data-testid="date-picker"
                classes={{ root: input }}
                selectedDate={selectedDate}
                fullWidth
                onDateChange={onDateChange}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                disabled={timeslotsUnavailable}
                shouldDisableDate={shouldDisableDate}
                variant="outlined"
                format={dateFormat}
                KeyboardButtonProps={{
                  'data-testid': 'date-icon',
                  onFocus: () => setIsOpen(true),
                }}
                PopoverProps={{
                  disableRestoreFocus: true,
                  'data-testid': 'date-popover',
                  onClose: () => setIsOpen(false),
                }}
                InputProps={{
                  onFocus: () => setIsOpen(true),
                }}
                open={isOpen}
              />

              <DayPicker
                startDate={MIN_DATE}
                lastDate={MAX_DATE}
                onSelect={setDate}
                selectedDate={selectedDate ? moment(selectedDate) : undefined}
              />
              <TimeSlots date={selectedDate} dateFrom={MIN_DATE} dateTo={MAX_DATE} isExactTime={isExactTime} />
            </>
          </ScrollableContent>
        </>
      }
      Buttons={
        <Button
          version="v2"
          onClick={handleNextClick}
          size="medium"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={isNextButtonDisabled}
        >
          Next
        </Button>
      }
    />
  );
};
